import { CONFIGURATIONS } from "../config/envConfig";


let state = {
  gtmLoaded: false,
};

/**
 * Initializes Google Consent Mode v2 with default 'denied' state.
 * Should be called as early as possible.
 */
export const initializeConsentMode = () => {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };

  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });

  window.gtag("js", new Date());
};

/**
 * Injects GTM container script into the document head.
 */
export const injectGTM = () => {
  if (
    typeof window === "undefined" ||
    state.gtmLoaded ||
    !CONFIGURATIONS.GTM_ID
  )
    return;

  const gtmId = CONFIGURATIONS.GTM_ID;
  const scriptId = "gtm-script";

  if (document.getElementById(scriptId)) return;

  (function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    var f = d.getElementsByTagName(s)[0],
      j = d.createElement(s),
      dl = l !== "dataLayer" ? "&l=" + l : "";
    j.id = scriptId;
    j.async = true;
    j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, "script", "dataLayer", gtmId);

  state.gtmLoaded = true;
  console.log("GTM Container Injected");
};

/**
 * Helper to clear cookies by name or pattern across all possible domain variations.
 */
const clearCookies = (patterns) => {
  if (typeof document === "undefined") return;

  const hostname = window.location.hostname;
  const isSecure = window.location.protocol === "https:";
  const cookies = document.cookie.split(";");

  const domains = [null, hostname, `.${hostname}`];
  const hostParts = hostname.split(".");
  if (hostParts.length > 2 && !/^\d+$/.test(hostParts[hostParts.length - 1])) {
    const rootDomain = hostParts.slice(-2).join(".");
    domains.push(rootDomain);
    domains.push(`.${rootDomain}`);
  }

  const paths = ["/", "", window.location.pathname];

  cookies.forEach((cookie) => {
    const trimmed = cookie.trim();
    if (!trimmed) return;
    const name = trimmed.split("=")[0].trim();
    const shouldDelete = patterns.some((pattern) =>
      pattern instanceof RegExp ? pattern.test(name) : name === pattern
    );
    if (shouldDelete) {
      domains.forEach((domain) => {
        paths.forEach((path) => {
          let s = `${name}=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path};`;
          if (domain) s += ` domain=${domain};`;
          document.cookie = s;
          document.cookie = s + " SameSite=Lax;";
          document.cookie = s + " SameSite=Strict;";
          if (isSecure) document.cookie = s + " SameSite=None; Secure;";
        });
      });
    }
  });
};

/**
 * Synchronizes Meta Pixel (fbq) state with the consent choice.
 * Uses a transparent proxy to capture initialization while maintaining gated execution.
 */
// this function is the on/off switch for Facebook's tracking pixel.
export const syncMetaConsent = (granted) => {
  if (typeof window === "undefined") return;

  // 1. Initialize the fbq command queue if it doesn't exist.
  // We keep .loaded as false to allow the real Meta script to initialize itself properly.
  if (!window.fbq) {
    const n = (window.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!window._fbq) window._fbq = n;
    n.push = n;
    n.loaded = false;
    n.version = "2.0";
    n.queue = [];

    // Initialize immediately, right after the stub exists, so any track()
    // calls queued afterward always have a pixel ID to attach to.
    // Must match the Pixel ID configured in GTM's "Meta Pixel Setup" tag.
    window.fbq("init", "1873248310231333");
  }

  if (!granted) {
    // 2. Revoke consent. Even if GTM fires the tag, data will be limited.
    window.fbq("consent", "revoke");
    console.log("Meta Pixel Consent: Revoked");
  } else {
    // 3. Grant consent.
    window.fbq("consent", "grant");
    console.log("Meta Pixel Consent: Granted");

    // 4. Check if the Meta script (fbevents.js) is physically loaded.
    // If not, we manually inject it to support toggling ON without a page refresh.
    const isScriptLoaded = !!document.querySelector(
      'script[src*="fbevents.js"]'
    );
    
    // We also check fbq.instance to see if the real script has taken over the queue.
    if (!isScriptLoaded) {
      console.log("Meta Pixel not detected. Injecting source script...");
      const t = document.createElement("script");
      t.async = true;
      t.src = "https://connect.facebook.net/en_US/fbevents.js";
      const s = document.getElementsByTagName("script")[0];
      s.parentNode.insertBefore(t, s);
    }
  }

  // 5. Update GTM Consent Mode v2 signals.
  if (window.gtag) {
    window.gtag("consent", "update", {
      ad_storage: granted ? "granted" : "denied",
      ad_user_data: granted ? "granted" : "denied",
      ad_personalization: granted ? "granted" : "denied",
    });
  }

  // Expose to window for testability/debugging
  if (typeof window !== "undefined") {
    window.syncMetaConsent = syncMetaConsent;
  }
};

/**
 * Single authoritative consent update for all four GTM Consent Mode v2 signals.
 * Use this whenever consent preferences change — it handles all combinations of
 * analytics/marketing correctly in ONE gtag call, avoiding race conditions.
 */
export const applyConsentSignals = (analytics, marketing) => {
  // 1. Gating order: Apply Stub/Revoke BEFORE injecting GTM
  // this function is the on/off switch for Facebook's tracking pixel.
  syncMetaConsent(marketing);

  if (analytics || marketing) {
    injectGTM();
  }

  // 2. Update GA signals
  if (window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: analytics ? "granted" : "denied",
    });
    window.dataLayer.push({ event: "consent_updated" });
  }

  // 3. Aggressive Cookie Purge
  if (!analytics) {
    const gaPatterns = ["_ga", "_gid", "_gat", /^_ga_/];
    clearCookies(gaPatterns);
    setTimeout(() => clearCookies(gaPatterns), 500);
    setTimeout(() => clearCookies(gaPatterns), 2000);
  }
  if (!marketing) {
    const pixelPatterns = ["_fbp", "_fbc"];
    clearCookies(pixelPatterns);
    setTimeout(() => clearCookies(pixelPatterns), 500);
    setTimeout(() => clearCookies(pixelPatterns), 2000);
  }

  console.log(`Consent applied — analytics: ${analytics}, marketing: ${marketing}`);

  // Expose to window for testability/debugging
  if (typeof window !== "undefined") {
    window.applyConsentSignals = applyConsentSignals;
  }
};

/**
 * Updates consent for GA4 via GTM (Consent Mode v2)
 */
export const enableGA = (analytics, marketing) => {
  console.log("--- enableGA called ---", { analytics, marketing });
  if (!analytics) {
    revokeAnalytics();
    return;
  }

  // Initialize and inject GTM if not already done
  initializeConsentMode();
  injectGTM();

  window.gtag("consent", "update", {
    analytics_storage: "granted",
    ad_storage: marketing ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied",
  });

  window.dataLayer.push({ event: "consent_updated" });
  console.log("Analytics Consent Updated (granted)");
};

/**
 * Updates consent for Meta Pixel via GTM (Consent Mode v2)
 */
export const enableMeta = (marketing) => {
  if (!marketing) {
    revokeMarketing();
    return;
  }

  // Initialize and inject GTM if not already done
  initializeConsentMode();
  injectGTM();

  window.gtag("consent", "update", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
  });

  window.dataLayer.push({ event: "consent_updated" });
  console.log("Marketing Consent Updated (granted)");
};


/**
 * Specifically revokes Analytics consent and clears GA cookies
 */
export const revokeAnalytics = () => {
  if (typeof window === "undefined") return;

  if (window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: "denied",
    });
    window.dataLayer.push({ event: "consent_updated" });
  }

  // Clear GA cookies patterns: _ga, _gid, _gat, and all variations of _ga_xxx
  const gaPatterns = ["_ga", "_gid", "_gat", /^_ga_/];

  // Clear immediately
  clearCookies(gaPatterns);

  // Retry after short delays to catch any cookies re-set by GTM during the transition
  setTimeout(() => clearCookies(gaPatterns), 500);
  setTimeout(() => clearCookies(gaPatterns), 2000);

  console.log("!!! ANALYTICS REVOKED: GA Cookies Targeted !!!");
};

/**
 * Specifically revokes Marketing consent and clears Pixel cookies
 */
export const revokeMarketing = () => {
  if (typeof window === "undefined") return;

  // Apply stub and revoke signals
  syncMetaConsent(false);
  
  if (window.gtag) {
    window.dataLayer.push({ event: "consent_updated" });
  }

  const pixelPatterns = ["_fbp", "_fbc"];

  // Clear immediately
  clearCookies(pixelPatterns);

  // Retry
  setTimeout(() => clearCookies(pixelPatterns), 500);
  setTimeout(() => clearCookies(pixelPatterns), 2000);

  console.log("Marketing Tracking Disabled (Sinkholed) & Cookies Cleared");
};

/**
 * Disables all tracking and clears all tracking cookies
 */
export const disableTracking = () => {
  revokeAnalytics();
  revokeMarketing();
};
